import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Image, Mail, Globe, Smartphone, CheckCircle2, ArrowRight, Zap, Layers } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const DigitalContentPage = () => {
  const navigate = useNavigate();

  const scrollToContact = () => {
    navigate('/', { state: { scrollTo: 'contact' } });
  };

  const features = [
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: "Social Media Content Creation",
      description: "Scroll-stopping posts that engage",
      detail: "We create platform-specific social content — captions, graphics, reels, and stories optimized for Instagram, Facebook, TikTok, LinkedIn, and Twitter."
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Blog Writing & SEO Content",
      description: "Content that ranks and converts",
      detail: "We write SEO-optimized blog posts, articles, and web copy that drive organic traffic, establish authority, and convert readers into customers."
    },
    {
      icon: <Mail className="w-8 h-8" />,
      title: "Email Marketing Content",
      description: "Emails that get opened and clicked",
      detail: "We craft compelling email campaigns — newsletters, product launches, promotions, and nurture sequences that drive engagement and sales."
    },
    {
      icon: <Image className="w-8 h-8" />,
      title: "Graphic Design & Visual Content",
      description: "Branded visuals that stand out",
      detail: "From social graphics to infographics and branded templates — we design visual content that captures attention and communicates your message."
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Website Copywriting",
      description: "Words that sell and convert",
      detail: "We write persuasive website copy — landing pages, product descriptions, about pages, and CTAs that turn visitors into customers."
    }
  ];

  const process = [
    {
      step: "01",
      title: "Content Strategy Development",
      description: "We define your content goals, target audience, key themes, and platform strategy to ensure every piece of content serves a purpose."
    },
    {
      step: "02",
      title: "Content Calendar Planning",
      description: "We create a comprehensive content calendar mapping out topics, formats, publishing schedules, and campaign themes."
    },
    {
      step: "03",
      title: "Content Creation & Production",
      description: "Our team writes, designs, and produces high-quality content aligned with your brand voice, optimized for each platform."
    },
    {
      step: "04",
      title: "Review & Approval Process",
      description: "You review drafts, provide feedback, and approve content before it goes live. We iterate until it's perfect."
    },
    {
      step: "05",
      title: "Publishing & Performance Tracking",
      description: "We publish content on schedule, monitor performance metrics, and optimize based on what resonates with your audience."
    }
  ];

  const benefits = [
    "Save time — we handle all content creation from ideation to publishing",
    "Maintain brand consistency across all digital channels",
    "Drive traffic, engagement, and conversions with strategic content",
    "Build authority and trust through valuable, relevant content",
    "Scale your content efforts without hiring a full team"
  ];

  const idealFor = [
    "Businesses needing consistent social media content",
    "Brands wanting to grow their blog and SEO presence",
    "Companies launching email marketing campaigns",
    "Freelancers and solopreneurs without in-house content teams",
    "Businesses wanting to scale content without sacrificing quality"
  ];

  return (
    <div className="min-h-screen bg-black">
      <Header onContactClick={scrollToContact} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-brand-brown/20 to-black"></div>
        <div className="absolute top-20 right-20 w-96 h-96 bg-brand-maroon/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-brand-olive/10 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-brand-maroon/20 rounded-full border border-brand-maroon/40 mb-6">
              <span className="text-brand-maroon font-bold">SERVICES</span>
            </div>
            <h1 className="text-6xl lg:text-7xl font-bold text-white mb-6 drop-shadow-[0_0_30px_rgba(255,255,255,0.5)]">
              Digital{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-maroon via-brand-olive to-brand-maroon drop-shadow-[0_0_20px_rgba(139,35,50,0.8)]">
                Content
              </span>
            </h1>
            <p className="text-2xl text-white leading-relaxed max-w-3xl mx-auto drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)]">
              High-quality content that drives traffic, engagement, and conversions. From social posts to blogs to email campaigns — we create content that works.
            </p>
          </div>
        </div>
      </section>

      {/* Wave Transition */}
      <div className="relative">
        <svg className="w-full h-24" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" fill="url(#gradient1)" />
          <defs>
            <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(139, 35, 50, 0.15)" />
              <stop offset="50%" stopColor="rgba(165, 166, 117, 0.15)" />
              <stop offset="100%" stopColor="rgba(139, 35, 50, 0.15)" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* What's Included Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-brand-brown/10 to-black">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-4 drop-shadow-[0_0_20px_rgba(255,255,255,0.4)]">
              What's{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-maroon to-brand-olive">
                Included
              </span>
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              Complete digital content services — from strategy to creation to publishing.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-brand-brown/60 to-black/80 rounded-3xl p-8 border-2 border-brand-olive/30 hover:border-brand-maroon/50 transition-all duration-300 hover:scale-105 shadow-2xl"
              >
                <div className="w-16 h-16 bg-brand-maroon/20 rounded-xl flex items-center justify-center mb-6 text-brand-maroon">
                  {feature.icon}
                </div>
                <h3 className="text-2xl font-bold text-white mb-2 drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]">
                  {feature.title}
                </h3>
                <p className="text-brand-olive font-semibold mb-3 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                  {feature.description}
                </p>
                <p className="text-white/80 leading-relaxed drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                  {feature.detail}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wave Transition */}
      <div className="relative">
        <svg className="w-full h-24" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" fill="url(#gradient2)" />
          <defs>
            <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(139, 35, 50, 0.15)" />
              <stop offset="50%" stopColor="rgba(165, 166, 117, 0.15)" />
              <stop offset="100%" stopColor="rgba(139, 35, 50, 0.15)" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Process Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-black to-brand-brown/10">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-4 drop-shadow-[0_0_20px_rgba(255,255,255,0.4)]">
              Our{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-maroon to-brand-olive">
                Process
              </span>
            </h2>
            <p className="text-xl text-white/80">
              From strategy to publishing — here's how we create content that drives results.
            </p>
          </div>

          <div className="space-y-8">
            {process.map((item, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-brand-brown/40 to-black/60 backdrop-blur-xl rounded-2xl p-8 border border-brand-olive/30 hover:border-brand-maroon/50 transition-all duration-300 shadow-lg flex items-start gap-6"
              >
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-brand-maroon to-brand-olive rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  {item.step}
                </div>
                <div className="flex-grow">
                  <h3 className="text-2xl font-bold text-white mb-3 drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]">
                    {item.title}
                  </h3>
                  <p className="text-white/80 leading-relaxed drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits & Ideal For Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-brand-brown/10 to-black">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Benefits */}
            <div className="bg-gradient-to-br from-brand-brown/60 to-black/80 rounded-3xl p-10 border-2 border-brand-maroon/50 shadow-2xl">
              <div className="flex items-center gap-3 mb-8">
                <Zap className="w-10 h-10 text-brand-maroon" />
                <h3 className="text-3xl font-bold text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.4)]">
                  Why It{' '}
                  <span className="text-brand-maroon">Works</span>
                </h3>
              </div>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-brand-maroon flex-shrink-0 mt-1" />
                    <span className="text-white/90 leading-relaxed drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                      {benefit}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Ideal For */}
            <div className="bg-gradient-to-br from-brand-brown/60 to-black/80 rounded-3xl p-10 border-2 border-brand-olive/50 shadow-2xl">
              <div className="flex items-center gap-3 mb-8">
                <Layers className="w-10 h-10 text-brand-olive" />
                <h3 className="text-3xl font-bold text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.4)]">
                  Ideal{' '}
                  <span className="text-brand-olive">For</span>
                </h3>
              </div>
              <ul className="space-y-4">
                {idealFor.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <ArrowRight className="w-6 h-6 text-brand-olive flex-shrink-0 mt-1" />
                    <span className="text-white/90 leading-relaxed drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-black to-brand-brown/10">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="bg-gradient-to-br from-brand-maroon/20 to-brand-olive/20 backdrop-blur-xl rounded-3xl p-12 border-2 border-brand-maroon/50 shadow-2xl">
            <h2 className="text-4xl font-bold text-white mb-6 drop-shadow-[0_0_20px_rgba(255,255,255,0.4)]">
              Ready to Scale Your{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-maroon to-brand-olive">
                Content Creation?
              </span>
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              Let's create high-quality digital content that engages your audience, drives traffic, and grows your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={scrollToContact}
                className="bg-brand-maroon hover:bg-brand-maroon/90 text-white font-bold text-lg px-12 py-6 rounded-xl transition-all duration-300 hover:scale-105 shadow-lg shadow-brand-maroon/50"
              >
                Book Free Content Consultation
              </Button>
              <Button
                onClick={() => navigate('/packages/basic')}
                variant="outline"
                className="border-2 border-brand-olive text-brand-olive hover:bg-brand-olive hover:text-black font-bold text-lg px-12 py-6 rounded-xl transition-all duration-300"
              >
                View Pricing Packages
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DigitalContentPage;